import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import axios from 'axios';

/**
 * Utility functions for Capacitor native features
 */

export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

export const getPlatform = () => {
  return Capacitor.getPlatform(); // 'ios', 'android', or 'web'
};

/**
 * Initialize Push Notifications
 *
 * NOTE Android: this plugin REQUIRES Firebase Cloud Messaging (FCM) configured
 * via `google-services.json`. Without it, calling PushNotifications.register()
 * throws `IllegalStateException: Default FirebaseApp is not initialized` at the
 * JVM level — that exception is NOT caught by the JS try/catch and crashes the
 * app immediately at boot. We therefore skip Android entirely until Firebase
 * is properly set up (tracked as P2 in the backlog).
 */
export const initializePushNotifications = async () => {
  if (!isNativePlatform()) {
    console.log('📱 Push notifications: Web platform - skipping');
    return;
  }

  // Skip Android until Firebase Cloud Messaging is configured.
  if (getPlatform() === 'android') {
    console.log('📱 Push notifications: Android FCM not configured yet - skipping');
    return;
  }

  try {
    // Request permission
    let permStatus = await PushNotifications.checkPermissions();
    console.log('📱 Push permission status:', permStatus.receive);

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.log('📱 Push notifications permission denied');
      return;
    }

    // Register with Apple/Google to get device token
    await PushNotifications.register();
    console.log('📱 Push notifications registration initiated');

    // Listen for registration success
    PushNotifications.addListener('registration', async (token) => {
      console.log('📱 Push registration success, token:', token.value?.substring(0, 20) + '...');
      
      // Store token locally for later registration
      localStorage.setItem('push_device_token', token.value);
      
      // Try to register with backend if user is authenticated
      await registerDeviceTokenWithBackend(token.value);
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('📱 Push registration error:', error);
    });

    // Listen for push notifications received
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('📱 Push notification received:', notification);
      // You can show a toast or update badge here
    });

    // Listen for push notification action (when user taps)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('📱 Push notification action:', action);
      // Navigate to relevant screen based on notification data
      const data = action.notification?.data;
      if (data?.type === 'message') {
        window.location.href = '/messages';
      } else if (data?.type === 'follow') {
        window.location.href = '/notifications';
      }
    });

  } catch (error) {
    console.error('📱 Error initializing push notifications:', error);
  }
};

/**
 * Register device token with backend (requires authentication)
 * Call this after user logs in.
 * Skipped on Android until Firebase Cloud Messaging is configured (see note
 * on initializePushNotifications above).
 */
export const registerDeviceTokenWithBackend = async (deviceToken = null) => {
  if (!isNativePlatform()) return;
  if (getPlatform() === 'android') {
    console.log('📱 Device token registration skipped on Android (no FCM yet)');
    return;
  }
  
  let token = deviceToken || localStorage.getItem('push_device_token');
  
  // If no token available, try to get one now
  if (!token) {
    console.log('📱 No device token cached, attempting to get one...');
    try {
      let permStatus = await PushNotifications.checkPermissions();
      console.log('📱 Permission status:', permStatus.receive);
      
      if (permStatus.receive === 'granted') {
        // Already have permission, register to get token
        await PushNotifications.register();
        
        // Wait a bit for the token to arrive
        await new Promise(resolve => setTimeout(resolve, 2000));
        token = localStorage.getItem('push_device_token');
      } else if (permStatus.receive === 'prompt') {
        // Request permission
        permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive === 'granted') {
          await PushNotifications.register();
          await new Promise(resolve => setTimeout(resolve, 2000));
          token = localStorage.getItem('push_device_token');
        }
      }
    } catch (err) {
      console.log('📱 Could not get push token:', err.message);
    }
  }
  
  if (!token) {
    console.log('📱 No device token available to register');
    return;
  }
  
  // The auth token will be added by axios interceptor in AuthContext
  // Just make the call - it will include the Authorization header automatically
  try {
    console.log('📱 Registering device token with backend...');
    const response = await axios.post('/api/push/register-device', {
      device_token: token,
      platform: getPlatform()
    });
    console.log('📱 Device token registered successfully:', response.data);
    return true;
  } catch (error) {
    console.error('📱 Error registering device token:', error?.response?.status, error?.response?.data || error.message);
    // Don't throw - this shouldn't block the login flow
    return false;
  }
};

/**
 * Initialize native features
 */
export const initializeNativeFeatures = async () => {
  if (!isNativePlatform()) return;

  try {
    // Configure Status Bar
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#000000' });

    // Hide splash screen after app loads - with timeout fallback
    try {
      await SplashScreen.hide();
      console.log('✅ Splash screen hidden successfully');
    } catch (splashError) {
      console.warn('⚠️ Could not hide splash screen:', splashError);
    }

    // Initialize Push Notifications
    await initializePushNotifications();

    // Add back button handler for Android
    if (getPlatform() === 'android') {
      App.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          App.exitApp();
        } else {
          window.history.back();
        }
      });
    }

    console.log('✅ Native features initialized');
  } catch (error) {
    console.error('Error initializing native features:', error);
    // Force hide splash screen even if there's an error
    try {
      await SplashScreen.hide();
    } catch (e) {
      console.warn('Could not force hide splash:', e);
    }
  }
};

/**
 * Force hide splash screen - call this as a fallback
 */
export const forceHideSplashScreen = async () => {
  if (!isNativePlatform()) return;
  try {
    await SplashScreen.hide();
  } catch (error) {
    console.warn('Force hide splash failed:', error);
  }
};

/**
 * Check if running on iOS
 */
export const isIOS = () => {
  return getPlatform() === 'ios';
};

/**
 * Check if running on Android
 */
export const isAndroid = () => {
  return getPlatform() === 'android';
};

/**
 * Get safe area insets for iOS notch
 */
export const getSafeAreaInsets = () => {
  if (isIOS()) {
    return {
      top: 'env(safe-area-inset-top)',
      bottom: 'env(safe-area-inset-bottom)',
      left: 'env(safe-area-inset-left)',
      right: 'env(safe-area-inset-right)'
    };
  }
  return { top: '0px', bottom: '0px', left: '0px', right: '0px' };
};
